import { useEffect, useRef, useState } from "react";
import { collection, getCountFromServer, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../../api/firebase";

// 市区町村まで絞る精密な問い合わせが通らないとき（複合インデックス未作成）に、
// 都道府県だけで引いた先頭この件数の中から手元で市区町村を絞る
const FALLBACK_SCAN = 60;

/**
 * 神社名の前方一致検索（shrines は全員 read 可）。名前だけでは同名社が多すぎるので
 * （八幡神社 3,022・稲荷神社 1,537・愛宕神社 502…）、土地で絞れるようにしてある。
 *
 *   - prefecture: shrines.prefecture と等値（既存の複合インデックス prefecture+name）
 *   - city:       shrines.city（"京都府京都市" のように都道府県から始まる）を
 *                 prefecture+city の前方一致で絞る。name との二重の範囲条件になるので
 *                 複合インデックス prefecture+name+city が要る（firestore.indexes.json）。
 *                 無いときは都道府県だけで引いて手元で絞る（取りこぼしあり）。
 *
 * 戻り値: { results, searching, total }
 *   total は今の絞り込みでの総数（count 集計）。取れないときは null。
 */
export default function useShrineNameSearch(text, { prefecture = "", city = "", max = 8, delay = 280 } = {}) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    const p = (text || "").trim();
    const pref = (prefecture || "").trim();
    const cityQ = pref ? (city || "").trim() : "";
    if (p.length < 1) {
      setResults([]);
      setTotal(null);
      setSearching(false);
      return undefined;
    }
    const id = ++seq.current;
    setSearching(true);

    const col = collection(db, "shrines");
    const base = [];
    if (pref) base.push(where("prefecture", "==", pref));
    base.push(where("name", ">=", p), where("name", "<=", p + ""));
    const precise = cityQ
      ? [...base, where("city", ">=", pref + cityQ), where("city", "<=", pref + cityQ + ""), orderBy("name"), orderBy("city")]
      : [...base, orderBy("name")];

    const toRow = (d) => {
      const x = d.data() || {};
      return { shrineId: d.id, name: x.name || "", prefecture: x.prefecture || "", city: x.city || "" };
    };

    const t = setTimeout(async () => {
      let rows = [];
      let count = null;
      try {
        const snap = await getDocs(query(col, ...precise, limit(max)));
        rows = snap.docs.map(toRow);
        try {
          const c = await getCountFromServer(query(col, ...precise));
          count = c.data().count;
        } catch (e) {
          count = null;
        }
      } catch (e) {
        if (cityQ) {
          // 精密な問い合わせが通らない（インデックス未作成など）→ 都道府県だけで引いて手元で絞る
          try {
            const snap = await getDocs(query(col, ...base, orderBy("name"), limit(FALLBACK_SCAN)));
            // city は "京都府京都市" の形。都道府県名を含めて前方一致させる（"京都" が "京都府" に当たらないように）
            rows = snap.docs.map(toRow).filter((r) => r.city.startsWith(pref + cityQ)).slice(0, max);
          } catch (e2) {
            rows = [];
          }
        }
      }
      if (id !== seq.current) return;
      setResults(rows);
      setTotal(count);
      setSearching(false);
    }, delay);
    return () => clearTimeout(t);
  }, [text, prefecture, city, max, delay]);

  return { results, searching, total };
}
